import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (config: Omit<ToastMessage, 'id'>) => string;
  showSuccess: (message: string) => string;
  showError: (message: string) => string;
  showWarning: (message: string) => string;
  showInfo: (message: string) => string;
  hideToast: (id: string) => void;
  clearAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((config: Omit<ToastMessage, 'id'>): string => {
    const id = Math.random().toString(36).substring(2, 9);
    const toast: ToastMessage = { ...config, id };
    setToasts((prev) => [...prev, toast]);

    // Auto-hide after duration
    const duration = config.duration || 3000;
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);

    return id;
  }, []);

  const showSuccess = useCallback((message: string) => showToast({ type: 'success', message }), [showToast]);
  const showError = useCallback((message: string) => showToast({ type: 'error', message }), [showToast]);
  const showWarning = useCallback((message: string) => showToast({ type: 'warning', message }), [showToast]);
  const showInfo = useCallback((message: string) => showToast({ type: 'info', message }), [showToast]);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Listen for custom toast events from non-React code (e.g., hooks)
  useEffect(() => {
    const handleToastError = (e: CustomEvent<{ message: string }>) => {
      showError(e.detail.message);
    };
    window.addEventListener('cubemeet-toast-error', handleToastError as EventListener);
    return () => {
      window.removeEventListener('cubemeet-toast-error', handleToastError as EventListener);
    };
  }, [showError]);

  const value: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideToast,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast Container */}
      <div style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={{
              padding: '12px 20px',
              borderRadius: 8,
              color: '#fff',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              backgroundColor: 
                toast.type === 'success' ? '#22c55e' :
                toast.type === 'error' ? '#ef4444' :
                toast.type === 'warning' ? '#f59e0b' : '#3b82f6',
            }}
            onClick={() => hideToast(toast.id)}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToastContext = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

export const useGlobalToast = (): ToastContextType => {
  return useToastContext();
};

export default ToastProvider;
